import React from 'react';
import { Link } from 'react-router-dom';
import { Video } from 'lucide-react';

function VideoHighlightsSection({ videos }) {
  // Extract YouTube video ID function
  const getYouTubeInfo = (url) => {
    if (!url) return { id: null, thumbnailUrl: null };

    // Regular expressions to match YouTube URL patterns
    const regexps = [
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)(?:&.*)?/,
      /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([a-zA-Z0-9_-]+)(?:\?.*)?/,
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]+)(?:\?.*)?/,
    ];

    let videoId = null;

    // Try each regex pattern to extract video ID
    for (const regex of regexps) {
      const match = url.match(regex);
      if (match && match[1]) {
        videoId = match[1];
        break;
      }
    }

    // Return video ID and thumbnail URL if found
    if (videoId) {
      return {
        id: videoId,
        thumbnailUrl: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
      };
    }

    return { id: null, thumbnailUrl: null };
  };

  return (
    <section className="py-12 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-white">Training Videos</h2>
          <Link to="/videos" className="text-yellow-400 hover:text-yellow-300 font-medium">
            View All Videos →
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {videos &&
            videos.map((video) => {
              // Get YouTube thumbnail if it's a YouTube video
              const { thumbnailUrl: ytThumbnail } =
                video.type === 'youtube' ? getYouTubeInfo(video.url) : { thumbnailUrl: null };

              return (
                <Link
                  key={video._id}
                  to={`/videos/${video._id}`}
                  className="bg-gray-900 rounded-lg overflow-hidden border border-gray-800 hover:border-yellow-600 transition-all flex flex-col"
                >
                  <div className="relative h-64">
                    <img
                      src={video.thumbnailUrl || ytThumbnail || '/video-placeholder.jpg'}
                      alt={video.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Fallback if image fails to load
                        e.currentTarget.src = '/video-placeholder.jpg';
                      }}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                      <div className="bg-yellow-500 rounded-full p-3">
                        <Video size={24} className="text-gray-900" />
                      </div>
                    </div>
                    <div className="absolute bottom-2 right-2 bg-gray-900 text-white text-xs px-2 py-1 rounded">
                      {video.type === 'youtube' ? 'YouTube' : 'MP4'}
                    </div>
                  </div>
                  <div className="p-4 flex-grow">
                    <h3 className="font-bold text-white text-lg mb-2">{video.title}</h3>
                    <p className="text-gray-400 text-sm line-clamp-2">{video.description}</p>
                    <div className="mt-3">
                      <span className="bg-gray-800 text-yellow-400 text-xs font-medium px-2.5 py-0.5 rounded">
                        {video.category}
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/videos"
            className="bg-gray-800 hover:bg-gray-700 text-white border border-yellow-600 px-6 py-3 rounded-md font-medium inline-block"
          >
            Browse All Training Videos
          </Link>
        </div>
      </div>
    </section>
  );
}

export default VideoHighlightsSection;
